import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Crown, QrCode, ImagePlus, X,
} from "lucide-react";
import { compressImage } from "@/lib/imageCompress";

const empty = {
  name: "", description: "", price_php: 0, billing_cycle: "monthly", duration_days: 30,
  is_free: false, is_active: true, show_on_landing: true, sort_order: 0, modules: [],
  payment_link: "", qr_image_base64: "", payment_instructions: "", badge: "",
  is_recommended: false,
};

export default function AdminSubscriptionsPage() {
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/admin/subscription-plans"),
        api.get("/admin/subscription-modules-catalog"),
      ]);
      setPlans(p.data?.items || []);
      setCatalog(c.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const groupedCatalog = React.useMemo(() => {
    const out = {};
    for (const m of catalog) {
      (out[m.group] = out[m.group] || []).push(m);
    }
    return out;
  }, [catalog]);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty, ...p,
      description: p.description || "", payment_link: p.payment_link || "",
      qr_image_base64: p.qr_image_base64 || "", payment_instructions: p.payment_instructions || "",
      badge: p.badge || "",
    });
    setOpen(true);
  };

  const toggleMod = (k) => setForm((f) => ({
    ...f, modules: f.modules.includes(k) ? f.modules.filter((x) => x !== k) : [...f.modules, k],
  }));
  const setAllInGroup = (group, on) => {
    const keys = (groupedCatalog[group] || []).map((m) => m.key);
    setForm((f) => {
      const s = new Set(f.modules);
      if (on) keys.forEach((k) => s.add(k)); else keys.forEach((k) => s.delete(k));
      return { ...f, modules: [...s] };
    });
  };

  const handleQR = async (file) => {
    if (!file) return;
    try {
      const b64 = await compressImage(file, { maxKB: 500, maxDim: 800 });
      setForm((f) => ({ ...f, qr_image_base64: b64 }));
    } catch (e) { toast.error("Could not load QR image"); }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = { ...form, price_php: Number(form.price_php), duration_days: Number(form.duration_days) };
      if (editing) await api.patch(`/admin/subscription-plans/${editing.id}`, payload);
      else await api.post("/admin/subscription-plans", payload);
      toast.success(editing ? "Plan updated" : "Plan created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removePlan = async (p) => {
    if (!window.confirm(`Delete the "${p.name}" plan?`)) return;
    try { await api.delete(`/admin/subscription-plans/${p.id}`); toast.success("Plan deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="space-y-5" data-testid="admin-subscriptions-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs text-slate-400 uppercase font-heading">Billing</p>
            <h1 className="font-heading text-3xl font-bold">Subscription Plans</h1>
            <p className="text-sm text-brand-inkMuted mt-1">Define the plans displayed on the landing page and shown to school owners.</p>
          </div>
          <Button onClick={startCreate} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="plan-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Plan
          </Button>
        </div>

        {loading ? <p className="text-center text-slate-500 py-8">Loading…</p> : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {plans.map((p) => (
              <Card key={p.id} className="p-5 rounded-3xl border-[#2563EB]/10 space-y-3" data-testid={`plan-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    {p.is_recommended && <span className="text-[10px] uppercase font-bold tracking-wider text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-full px-2 py-0.5 mr-1">★ Recommended</span>}
                    {p.badge && <span className="text-[10px] uppercase font-bold tracking-wider text-[#1D4ED8] bg-[#DBEAFE] rounded-full px-2 py-0.5">{p.badge}</span>}
                    <h3 className="font-heading text-xl font-bold mt-1">{p.name}</h3>
                    <p className="text-xs text-slate-500">
                      {p.is_free ? "Free plan" : `₱${(p.price_php || 0).toLocaleString()} · ${p.billing_cycle} · ${p.duration_days}d`}
                    </p>
                  </div>
                  {p.is_free && <Crown className="w-5 h-5 text-amber-500" />}
                </div>
                {p.description && <p className="text-sm text-slate-700 line-clamp-3">{p.description}</p>}
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[11px] bg-slate-100 px-2 py-0.5 rounded-full">{p.modules?.length || 0} modules</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.is_active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
                    {p.is_active ? "Active" : "Inactive"}
                  </span>
                  {p.show_on_landing && <span className="text-[11px] bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full">On landing</span>}
                  {p.payment_link && <span className="text-[11px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">Link</span>}
                  {p.qr_image_base64 && <span className="text-[11px] bg-fuchsia-50 text-fuchsia-700 px-2 py-0.5 rounded-full">QR</span>}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => startEdit(p)} data-testid={`plan-edit-${p.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  {!p.is_free && <Button variant="ghost" size="sm" className="text-rose-500" onClick={() => removePlan(p)} data-testid={`plan-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit "${editing.name}"` : "Create plan"}</DialogTitle>
            <DialogDescription>Configure pricing, payment details, and the modules included in this plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" /></div>
              <div><Label>Badge (optional)</Label><Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder='Type "Recommended" to mark this plan as featured' data-testid="plan-badge-input" /><p className="text-xs text-slate-500 mt-1">Tip: enter <strong>Recommended</strong>, <strong>Most popular</strong>, or <strong>Featured</strong> to highlight this plan on the landing page with a gradient + ★ badge.</p></div>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div><Label>Price (₱)</Label><Input type="number" min="0" value={form.price_php} onChange={(e) => setForm({ ...form, price_php: e.target.value })} disabled={form.is_free} /></div>
              <div>
                <Label>Cycle</Label>
                <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Duration (days)</Label><Input type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} /></div>
              <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> Active</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.show_on_landing} onCheckedChange={(v) => setForm({ ...form, show_on_landing: v })} /> Show on landing</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v, price_php: v ? 0 : form.price_php })} /> Free plan</label>
              <label className="flex items-center gap-2 text-sm" data-testid="plan-recommended-toggle"><Switch checked={!!form.is_recommended} onCheckedChange={(v) => setForm({ ...form, is_recommended: v })} /> <span className="inline-flex items-center gap-1">★ Recommended <span className="text-xs text-slate-400 font-normal">(only one plan)</span></span></label>
            </div>

            <div className="border border-slate-200 rounded-2xl p-3 space-y-2">
              <div className="flex items-center justify-between"><Label className="m-0">Payment details</Label></div>
              <div className="grid sm:grid-cols-2 gap-3 items-start">
                <div className="space-y-2">
                  <div><Label>Payment link (PayMongo / GCash URL)</Label><Input value={form.payment_link} onChange={(e) => setForm({ ...form, payment_link: e.target.value })} placeholder="https://pay.example.com/..." /></div>
                  <div>
                    <Label>QR image</Label>
                    {form.qr_image_base64 ? (
                      <div className="flex items-start gap-2 mt-1">
                        <img src={form.qr_image_base64} alt="QR" className="w-24 h-24 rounded-2xl object-cover border border-slate-200" />
                        <button type="button" onClick={() => setForm({ ...form, qr_image_base64: "" })} className="text-xs text-rose-500 hover:underline"><X className="w-3.5 h-3.5 inline" /> Remove</button>
                      </div>
                    ) : (
                      <label className="mt-1 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 cursor-pointer">
                        <QrCode className="w-4 h-4" /> Upload QR
                        <input type="file" accept="image/*" hidden onChange={(e) => handleQR(e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                </div>
                <div><Label>Payment instructions (shown to owners)</Label><Textarea rows={6} value={form.payment_instructions} onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })} placeholder="1. Scan the QR / click the link.&#10;2. Enter ₱X amount.&#10;3. Upload screenshot + reference number." /></div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-2xl p-3 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="m-0">Modules included <span className="text-xs text-slate-500 font-normal">({form.modules.length} selected)</span></Label>
              </div>
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {Object.keys(groupedCatalog).map((g) => {
                  const groupKeys = groupedCatalog[g].map((m) => m.key);
                  const selected = groupKeys.filter((k) => form.modules.includes(k)).length;
                  const allOn = selected === groupKeys.length;
                  return (
                    <div key={g} className="rounded-xl border border-slate-100 p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-700">{g} <span className="text-xs text-slate-400 font-normal">({selected}/{groupKeys.length})</span></span>
                        <button type="button" onClick={() => setAllInGroup(g, !allOn)} className="text-xs text-[#1D4ED8] hover:underline">{allOn ? "Unselect all" : "Select all"}</button>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-1.5">
                        {groupedCatalog[g].map((m) => (
                          <label key={m.key} className="flex items-center gap-2 text-xs border border-slate-100 rounded-xl px-2 py-1.5 cursor-pointer hover:bg-slate-50">
                            <input type="checkbox" checked={form.modules.includes(m.key)} onChange={() => toggleMod(m.key)} />
                            <span className="truncate">{m.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#2563EB]" data-testid="plan-save-btn">{editing ? "Save changes" : "Create plan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
